/**
 * sendOutcomePolicy.ts — who speaks after a send decision, and when.
 *
 * Leaf module on purpose (same reasoning as sendFailureRetry.ts): imported by
 * the receipt builder AND by GlobalChatSystem's `prest0n:action-decided`
 * listener, without dragging either layer's dependency graph into the other.
 *
 * ── THE PROBLEM THIS SOLVES (owner report, 2026-08-24) ──
 *
 * A failed send posts a receipt card that already states, in writing: what
 * happened, why, and the next step. Then the `prest0n:action-decided`
 * listener fires a hidden turn asking Prest0n to "explain what happened and
 * give the single next step" — so the user watches an "Analyzing your
 * request…" spinner for twenty seconds and receives a paragraph restating the
 * card they are already looking at.
 *
 * That second turn is not always wrong. When Prest0n can DO something a
 * renderer cannot — put a reconnect card on screen, check the Sent folder,
 * propose a corrected address — it earns its place. When the card is the
 * whole truth and there is nothing left to offer, it is pure redundancy, and
 * worse: with no real facts to add, the model fills the space by inventing
 * some. The 2026-08-24 transcript has it asserting "Gmail is connected" —
 * something it never checked and had no way to know.
 *
 * So the rule is not "explain failures". It is: speak only when you can add
 * an action the card cannot.
 */

/**
 * Should a FAILED send decision spawn a Prest0n turn to explain it?
 *
 * `kind` is the outcome kind from the server
 * (functions/src/prest0nSession/sendConfirmation.ts) or one of the
 * decision-level kinds resolved client-side.
 */
export function failureNeedsExplainTurn(kind: string | null | undefined): boolean {
  switch (String(kind || '')) {
    // OUR configuration blocked it. Nothing is wrong with the user's account,
    // there is no setting for them to change, and the receipt already carries
    // the retry. Prest0n has nothing to add and no grounds to speculate —
    // this is the case that produced both the redundant paragraph and the
    // invented "Gmail is connected".
    case 'internal-error':
      return false;

    // The user chose this, or the card was already answered elsewhere. The
    // receipt is the whole record.
    case 'cancelled':
    case 'already-decided':
      return false;

    // A newer draft replaced this card (2026-08-25). The receipt names the
    // replacement and the newest card is right there — a turn would only
    // narrate furniture.
    case 'superseded':
      return false;

    // Everything else — not-connected (offer the reconnect), invalid-recipient
    // (propose the fix), unconfirmed/unreachable (check the Sent folder),
    // rate-limited, provider-rejected — has a real next action that only a
    // turn can take.
    default:
      return true;
  }
}
